import { AnchorButton, Icon, Intent, Tag, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as Sentry from '@sentry/browser'
import * as React from 'react'
import PrinterV2 from '../../printing/printer'
import { showFailure } from '../../Toast'
import { UprightStyle } from '../../types'
import { ProductV4 } from '../../types/api'
import PrintBarcodeButton from '../PrintBarcodeButton'
import { DrawerTable } from './DrawerTable'
import { pluralizeFromNumber } from '../../utils/string'
import { printableIds } from './utils'
import { formatMoneyWithCurrency } from '../../utils'

const flex: React.CSSProperties = { display: 'flex' }
const flexAlignCenter: React.CSSProperties = { ...flex, alignItems: 'center' }
const tableCell: React.CSSProperties = { ...flexAlignCenter, borderRight: 'none' }
const footerCell: React.CSSProperties = { margin: `20px 0px 12px 0px` }
const style: UprightStyle = {
  detailsContainer: { maxWidth: 'calc(50vw)' }, // This must match the drawer size, usually Drawer.SIZE_STANDARD
  tag: { marginLeft: 3 },
  spacer: { margin: `15px 0 30px 0` },
  tableHeader: { display: 'flex', alignItems: 'left', borderRight: 'none' },
  emptyIcon: { height: 24 },
  tableCell: { ...flexAlignCenter, borderRight: 'none' },
  titleCell: { ...tableCell },
  titleCellSpan: { textOverflow: 'ellipsis', overflow: 'hidden', overflowWrap: 'break-word' },
  skuCell: { ...tableCell },
  footerCell,
  totalQuantityLabelFooterCell: { ...footerCell, fontWeight: 500 },
  totalQuantityFooterCell: { ...footerCell, fontWeight: 700 },
  printAllButton: { paddingLeft: 5 }
}

// See react-table/README.md for column props
const tableColumns = (totalVariantQuantity, handlePrintAllBarcodes, productOverriddenValues) => {
  return [
    {
      id: 'title',
      Header: 'Variant',
      headerStyle: style.tableHeader,
      sortable: true,
      minWidth: 160,
      style: style.titleCell,
      accessor: d => (
        <span title={d.title} style={style.titleCellSpan}>
          {d.title}
        </span>
      ),
      Footer: <div style={style.totalQuantityLabelFooterCell}>Total Quantity</div>
    },
    {
      id: 'quantity',
      Header: 'Quantity',
      headerStyle: style.tableHeader,
      sortable: true,
      maxWidth: 70,
      style: style.tableCell,
      accessor: d => d.quantity,
      Footer: <div style={style.totalQuantityFooterCell}>{totalVariantQuantity}</div>
    },
    {
      id: 'sku',
      Header: 'SKU',
      headerStyle: { ...style.tableHeader },
      sortable: true,
      minWidth: 152, // must accommodate the width of the "print all barcodes button"
      style: style.skuCell,
      accessor: d => {
        return (
          <div title={d.sku}>
            {d.sku ? (
              <PrintBarcodeButton buttonText="" productId={d.id} productSkuLength={d.sku?.length} />
            ) : (
              <div style={style.emptyIcon}>—</div>
            )}
            {d.sku}
          </div>
        )
      },
      Footer: (
        <div style={style.footerCell}>
          <AnchorButton
            onClick={() => handlePrintAllBarcodes()}
            icon={IconNames.PRINT}
            intent={Intent.PRIMARY}
            style={style.printAllButton}
            small
            minimal
          >
            Print All Barcodes
          </AnchorButton>
        </div>
      )
    },
    {
      id: 'price',
      Header: 'Price',
      headerStyle: style.tableHeader,
      sortable: true,
      maxWidth: 84,
      style: style.tableCell,
      accessor: d => {
        if (!d.price) return '-'
        const formattedPrice = formatMoneyWithCurrency(d.price, 'USD')
        if (!productOverriddenValues) return formattedPrice

        return (
          <Tooltip content="Price override is active">
            <div style={flex}>
              <span>{formattedPrice}</span>
              <Icon icon={IconNames.TAG} />
            </div>
          </Tooltip>
        )
      }
    },
    {
      id: 'inventoryLocation',
      Header: 'Location',
      headerStyle: style.tableHeader,
      sortable: true,
      minWidth: 150,
      style: style.tableCell,
      accessor: d => {
        return (
          <span title={d.inventoryLocation?.name ? d.inventoryLocation?.name : null}>
            {d.inventoryLocation?.name || '—'}
          </span>
        )
      }
    }
  ]
}

export const VariantDetails = ({ product }: { product: ProductV4 }) => {
  const productVariants = React.useMemo(() => product.variants || [], [product.variants])
  const productVariantsCount = productVariants.length
  const pageSize = 12
  const totalQuantity = React.useMemo(
    () => productVariants.map(pv => pv.quantity).reduce((acc, v) => acc + (v || 0), 0),
    [productVariants]
  )

  const printBarcodes = React.useCallback(async () => {
    const printer = new PrinterV2()

    try {
      await printer.printProductLabelFromId(printableIds(product))
    } catch (e) {
      showFailure('Unable to print barcodes')
      Sentry.captureException(e)
    }
  }, [product])

  const columns = React.useMemo(
    () => tableColumns(totalQuantity, printBarcodes, product.overriddenValues),
    [totalQuantity, printBarcodes, product.overriddenValues]
  )

  return (
    <div style={style.detailsContainer}>
      <div className="drawer-header">
        <Icon icon={IconNames.LAYOUT_AUTO} /> Variants{' '}
        <Tag minimal round style={style.tag}>
          {productVariantsCount}
        </Tag>
      </div>
      {productVariantsCount === 0 ? (
        <p className="non-ideal">No variants yet</p>
      ) : (
        <DrawerTable
          tableProps={{ data: productVariants, columns }}
          dataLabel={`Variant${pluralizeFromNumber(productVariants.length)}`}
          pageSize={pageSize}
        />
      )}
    </div>
  )
}
